import { ResponseData } from "./types";

interface QueuedRequest {
  path: string;
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  options: {
    auth?: boolean;
    body?: Record<string, any>;
    form?: FormData;
    responseType?: "json" | "blob";
  };
  resolve: (value: ResponseData<any>) => void;
  reject: (reason?: any) => void;
}

class HttpClient {
  private baseUrl: string;
  private isRefreshing = false;
  private requestQueue: QueuedRequest[] = [];

  constructor() {
    this.baseUrl = "https://api.pescavariadagoya.com.ar/api";//process.env.REACT_APP_API_URL || "";
  }

  private async makeRequest(
    path: string,
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
    options: {
      auth?: boolean;
      body?: Record<string, any>;
      form?: FormData;
      responseType?: "json" | "blob";
    } = {}
  ): Promise<ResponseData<any>> {
    const { auth = false, body, form, responseType = "json" } = options;

    if (this.isRefreshing && auth) {
      return this.queueRequest(path, method, options);
    }

    const url = `${this.baseUrl}${path}`;
    const headers = new Headers();

    if (auth) {
      const accessToken = localStorage.getItem("accessToken");
      if (accessToken) {
        headers.set("Authorization", `Bearer ${accessToken}`);
      }
    }

    const payload: RequestInit = {
      method,
      headers,
    };

    if (body) {
      payload.body = JSON.stringify(body);
      headers.set("Content-Type", "application/json");
    } else if (form) {
      payload.body = form;
    }

    try {
      const response = await fetch(url, payload);

      if (response.status === 401 && auth) {
        const errorBody = await response
          .clone()
          .json()
          .catch(() => ({}));

        if (errorBody.message === "Token expirado") {
          return this.handleTokenExpiration(
            path,
            method,
            options,
            url,
            payload
          );
        }
      }

      let data;
      if (responseType === "blob") {
        if (response.ok) {
          data = await response.blob();
        } else {
          const errorData = await response.json().catch(() => ({}));
          return {
            statusCode: response.status,
            statusText: response.statusText,
            message: errorData.message || response.statusText,
            data: null,
          };
        }
      } else {
        data = await response.json().catch(() => ({}));
        data = data.data || data;
      }

      return {
        statusCode: response.status,
        statusText: response.statusText,
        message:
          responseType === "blob"
            ? "PDF generated successfully"
            : data.message || response.statusText,
        data: data,
      };
    } catch (error) {
      return {
        statusCode: 500,
        statusText: "Error de red",
        message: error instanceof Error ? error.message : "Unknown error",
        data: null,
      };
    }
  }

  private queueRequest(
    path: string,
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
    options: {
      auth?: boolean;
      body?: Record<string, any>;
      form?: FormData;
      responseType?: "json" | "blob";
    }
  ): Promise<ResponseData<any>> {
    return new Promise((resolve, reject) => {
      this.requestQueue.push({
        path,
        method,
        options,
        resolve,
        reject,
      });
    });
  }

  private async handleTokenExpiration(
    path: string,
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
    options: {
      auth?: boolean;
      body?: Record<string, any>;
      form?: FormData;
      responseType?: "json" | "blob";
    },
    url: string,
    payload: RequestInit
  ): Promise<ResponseData<any>> {
    if (this.isRefreshing) {
      return this.queueRequest(path, method, options);
    }

    this.isRefreshing = true;
    const refreshed = await this.refreshToken();

    if (refreshed) {
      const newToken = localStorage.getItem("accessToken");
      if (newToken && payload.headers) {
        (payload.headers as Headers).set("Authorization", `Bearer ${newToken}`);
        const retryResponse = await fetch(url, payload);

        let retryData;
        if (options.responseType === "blob") {
          retryData = retryResponse.ok ? await retryResponse.blob() : null;
        } else {
          retryData = await retryResponse.json().catch(() => ({}));
        }

        await this.processQueue();

        return {
          statusCode: retryResponse.status,
          statusText: retryResponse.statusText,
          message:
            options.responseType === "blob"
              ? "PDF generated successfully"
              : retryData.message || retryResponse.statusText,
          data: retryData?.data || retryData,
        };
      }
    } else {
      this.rejectQueue(new Error("Token refresh failed"));
    }

    return {
      statusCode: 401,
      statusText: "Unauthorized",
      message: "Token refresh failed",
      data: null,
    };
  }

  private async refreshToken(): Promise<boolean> {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      this.isRefreshing = false;
      return false;
    }

    try {
      const response = await fetch(`${this.baseUrl}/auth/refresh-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data?.accessToken) {
          localStorage.setItem("accessToken", data.data.accessToken);
          localStorage.setItem("refreshToken", refreshToken);
          this.isRefreshing = false;
          return true;
        }
      }
    } catch (error) {}

    this.isRefreshing = false;
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    window.location.reload();
    return false;
  }

  private async processQueue(): Promise<void> {
    const queue = [...this.requestQueue];
    this.requestQueue = [];

    const promises = queue.map(async (queuedRequest) => {
      try {
        const result = await this.makeRequest(
          queuedRequest.path,
          queuedRequest.method,
          queuedRequest.options
        );
        queuedRequest.resolve(result);
      } catch (error) {
        queuedRequest.reject(error);
      }
    });

    await Promise.all(promises);
  }

  private rejectQueue(error: Error): void {
    const queue = [...this.requestQueue];
    this.requestQueue = [];

    queue.forEach((queuedRequest) => {
      queuedRequest.reject(error);
    });
  }

  async get(path: string, auth = false): Promise<ResponseData<any>> {
    return this.makeRequest(path, "GET", { auth });
  }

  async getBlob(path: string, auth = false): Promise<ResponseData<Blob>> {
    return this.makeRequest(path, "GET", { auth, responseType: "blob" });
  }

  async post(
    path: string,
    body?: Record<string, any>,
    auth = false
  ): Promise<ResponseData<any>> {
    return this.makeRequest(path, "POST", { auth, body });
  }

  async put(
    path: string,
    body?: Record<string, any>,
    auth = false
  ): Promise<ResponseData<any>> {
    return this.makeRequest(path, "PUT", { auth, body });
  }

  async patch(
    path: string,
    body?: Record<string, any>,
    auth = false
  ): Promise<ResponseData<any>> {
    return this.makeRequest(path, "PATCH", { auth, body });
  }

  async delete(path: string, auth = false): Promise<ResponseData<any>> {
    return this.makeRequest(path, "DELETE", { auth });
  }

  async postForm(
    path: string,
    form: FormData,
    auth = false
  ): Promise<ResponseData<any>> {
    return this.makeRequest(path, "POST", { auth, form });
  }
}

export const httpClient = new HttpClient();
